import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import {
  EventAvailable as UpcomingIcon,
  History as HistoryIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { formatDate, formatTime } from '../../utils/formatters';
import { getAppointmentStatusStyles, getAppointmentStatusLabel } from '../../constants/statusConfig';
import { usePersonAppointments } from '../../hooks/queries/usePersonDetail';
import LoadingState from '../ui/LoadingState';

function deriveStatus(appt) {
  if (appt.cancelledAt) return 'cancelled';
  if (appt.attendedAt) return 'completed';
  const isPast = new Date(appt.startTime).getTime() < Date.now();
  if (isPast && appt.confirmedAt && !appt.attendedAt) return 'no_show';
  if (appt.confirmedAt) return 'confirmed';
  return 'pending';
}

function AppointmentRow({ appt, highlight = false }) {
  const status = deriveStatus(appt);
  const styles = getAppointmentStatusStyles(status);
  return (
    <Box
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: highlight ? 'primary.main' : styles.borderColor,
        bgcolor: highlight ? 'primary.50' : styles.bgcolor,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          {formatDate(appt.startTime)} · {formatTime(appt.startTime)}
        </Typography>
        <Chip label={getAppointmentStatusLabel(status)} size="small" color={styles.chipColor} sx={{ height: 18, fontSize: '0.65rem' }} />
      </Box>
      {appt.service?.name && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {appt.service.name}
        </Typography>
      )}
      {appt.schedule && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {appt.schedule.professionalName}{appt.schedule.placeName ? ` · 📍 ${appt.schedule.placeName}` : ''}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Timeline de agendamentos de uma Person: próximo (destacado) + 3 últimos, com "ver todos".
 */
export default function PersonAppointmentsTimeline({ personId }) {
  const { appointments, loading } = usePersonAppointments(personId);
  const [showAll, setShowAll] = useState(false);

  const { next, upcomingRest, past } = useMemo(() => {
    const now = Date.now();
    const upcoming = appointments
      .filter((a) => new Date(a.startTime).getTime() >= now)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const pastList = appointments
      .filter((a) => new Date(a.startTime).getTime() < now)
      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    return { next: upcoming[0] || null, upcomingRest: upcoming.slice(1), past: pastList };
  }, [appointments]);

  if (loading) return <LoadingState message="Carregando agendamentos..." />;

  const hasAny = appointments.length > 0;
  const pastToShow = showAll ? past : past.slice(0, 3);
  const hasMore = past.length > 3 || upcomingRest.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CalendarIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight="bold">Agendamentos</Typography>
      </Box>

      {!hasAny && (
        <Typography variant="body2" color="text.secondary">Nenhum agendamento para esta pessoa.</Typography>
      )}

      {next && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <UpcomingIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="primary" fontWeight="bold">Próximo</Typography>
          </Box>
          <AppointmentRow appt={next} highlight />
        </Box>
      )}

      {showAll && upcomingRest.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">Próximos</Typography>
          {upcomingRest.map((a) => <AppointmentRow key={a.id} appt={a} />)}
        </Box>
      )}

      {past.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Histórico</Typography>
          </Box>
          {pastToShow.map((a) => <AppointmentRow key={a.id} appt={a} />)}
        </Box>
      )}

      {hasMore && (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="center">
            <Button size="small" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Mostrar menos' : `Ver todos (${appointments.length})`}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
